/**
 * CheckInModal (BON-204 R2) — partial per-booking workshop check-in. A centered
 * modal over a scrim (same chrome as CrewPickerModal): close via ×, Escape,
 * scrim click, or Done. Lists the workshop's ACTIVE (CONFIRMED, incl. comp)
 * bookings as rows; instructors tap a row to record how many of that party are
 * present. Party-of-1 rows toggle 0↔1 inline (immediate PUT); party>1 rows open
 * a stepper that commits ONE PUT on Confirm. Progress is by SEATS — "X / Y
 * guests in" + bar + "N to go". Search filters by customer name.
 *
 * Ordering (revised AC4): by last name then first name, with FULLY checked-in
 * bookings sunk to the bottom; re-sorts live after each confirm. Writes fire the
 * optimistic `useSetCheckIn` mutation, so the cache (and this list) update
 * instantly — the footer reads "Saved live". Mobile-friendly: full-width rows,
 * large hit targets, the stepper anchored to the bottom of the viewport.
 */
import { useEffect, useMemo, useState } from 'react';

import type { RosterBooking, WorkshopDetail } from '../../../../api/workshops';
import { MOBILE_MAX, useMediaQuery } from '../../../../lib/useMediaQuery';
import { useSetCheckIn } from '../useWorkshop';

import { CheckInModalCard } from './CheckInModalCard';
import { activeCheckInBookings, filterByName, sortForCheckIn } from './checkInModel';
import { CheckInStepper } from './CheckInStepper';
import { formatDate, formatTime } from './workshopFields';

export interface CheckInModalProps {
  workshop: WorkshopDetail;
  onClose: () => void;
  /** Where closing takes you, for the footer's "return to …" line. */
  returnsTo?: string;
}

/** Close on Escape while the modal is mounted. */
function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
}

/**
 * "<venue> · <local date> · <local time>" — the header staff read at the door.
 * When the venue-local column is unpopulated it says so instead of rendering
 * "<venue> · — · —", which reads as a layout artefact rather than missing data.
 */
function checkInTitle(workshop: WorkshopDetail): string {
  const local = workshop.localStartDatetime;
  if (!local) return `${workshop.venueName} · Date and time unavailable`;
  return `${workshop.venueName} · ${formatDate(local)} · ${formatTime(local)}`;
}

export function CheckInModal({ workshop, onClose, returnsTo = 'the workshop' }: CheckInModalProps) {
  const [query, setQuery] = useState('');
  const [stepperId, setStepperId] = useState<string | null>(null);
  const setCheckIn = useSetCheckIn(workshop.id);
  const pendingId = setCheckIn.isPending ? setCheckIn.variables?.bookingId : undefined;
  // Full-screen the modal on phones (instructors check in on mobile); a centered
  // card on tablet/desktop.
  const fullScreen = useMediaQuery(MOBILE_MAX);

  useEscape(onClose);

  const active = useMemo(
    () => activeCheckInBookings(workshop.roster.bookings),
    [workshop.roster.bookings],
  );
  const rows = useMemo(() => sortForCheckIn(filterByName(active, query)), [active, query]);
  const stepperBooking = useMemo(
    () => active.find((b) => b.bookingId === stepperId) ?? null,
    [active, stepperId],
  );

  // Party-of-1 inline toggle: immediate PUT with the next count (0 or 1).
  const onToggle = (b: RosterBooking, nextCount: number) =>
    setCheckIn.mutate({
      bookingId: b.bookingId,
      checkedInCount: nextCount,
      attendeeCount: b.attendeeCount,
    });

  // Stepper Confirm: ONE PUT with the chosen count, then close the sheet.
  const onConfirm = (count: number) => {
    if (!stepperBooking) return;
    setCheckIn.mutate({
      bookingId: stepperBooking.bookingId,
      checkedInCount: count,
      attendeeCount: stepperBooking.attendeeCount,
    });
    setStepperId(null);
  };

  const title = checkInTitle(workshop);

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.4)',
        display: 'flex',
        alignItems: fullScreen ? 'stretch' : 'center',
        justifyContent: 'center',
        zIndex: 60,
        padding: fullScreen ? 0 : 12,
      }}
    >
      <CheckInModalCard
        fullScreen={fullScreen}
        title={title}
        checkedInSeats={workshop.roster.checkedInSeats}
        confirmedAttendees={workshop.roster.confirmedAttendees}
        query={query}
        onQueryChange={setQuery}
        rows={rows}
        pendingId={pendingId}
        onToggle={onToggle}
        onOpenStepper={(b) => setStepperId(b.bookingId)}
        onClose={onClose}
        returnsTo={returnsTo}
      />
      {stepperBooking && (
        <CheckInStepper
          booking={stepperBooking}
          pending={pendingId === stepperBooking.bookingId}
          onConfirm={onConfirm}
          onClose={() => setStepperId(null)}
        />
      )}
    </div>
  );
}
